import { Link } from '@inertiajs/react'
import { ArrowRight, Check, ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import IconChip from './IconChip'
import { iconFor } from './moduleIcons'
import StatusBadge from './StatusBadge'
import type {
  TechnologyStackGroup,
  TechnologyStatus,
  TechnologyStatusLegendEntry,
} from '@/types/technology'

/* The full platform, filterable by what is actually shipped.
 *
 * A flat list of thirty-odd module names is the exact shape of page a buyer
 * skims and forgets. The filter turns it into something they can interrogate,
 * and the question they always want to ask first is the one it answers:
 * "fine, but what of this can I have TODAY?"
 *
 * Filtering by status rather than by pillar is the deliberate choice. Pillar is
 * already the visible grouping and each pillar has its own page; status is the
 * axis a reader cannot get at any other way, and the axis a vendor normally
 * makes hard to see. Exposing it as the primary control is the honesty
 * positioning turned into an interaction rather than a paragraph.
 *
 * Counts come off the real registry, so a filter can never advertise more
 * than it shows.
 *
 * On /technology/capabilities every module is serialized WITH its detail and
 * points, folded into a native <details> on its card: the audience pages show
 * many modules only as chips, so this page is where that copy stays in the
 * public HTML. Each card's id is its module key — those chips link here as
 * /technology/capabilities#<key>. The filter starts at "Everything", so every
 * anchor is in the server-rendered page.
 */

type Filter = TechnologyStatus | 'all'

export default function StackExplorer({
  groups,
  legend,
}: {
  groups: TechnologyStackGroup[]
  legend: TechnologyStatusLegendEntry[]
}) {
  const [filter, setFilter] = useState<Filter>('all')

  const counts = useMemo(() => {
    const all = groups.flatMap((group) => group.modules)
    return {
      all: all.length,
      live: all.filter((m) => m.status === 'live').length,
      building: all.filter((m) => m.status === 'building').length,
      planned: all.filter((m) => m.status === 'planned').length,
    } as Record<Filter, number>
  }, [groups])

  const visible = useMemo(
    () =>
      groups
        .map((group) => ({
          ...group,
          modules:
            filter === 'all'
              ? group.modules
              : group.modules.filter((module) => module.status === filter),
        }))
        // A pillar with nothing matching is dropped rather than shown empty —
        // an empty heading reads as a missing feature, not as a filter result.
        .filter((group) => group.modules.length > 0),
    [groups, filter]
  )

  const options: { value: Filter; label: string }[] = [
    { value: 'all', label: 'Everything' },
    ...legend.map((entry) => ({ value: entry.status as Filter, label: entry.label })),
  ]

  return (
    <div>
      <div
        role="group"
        aria-label="Filter the platform by what is shipped"
        className="flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const active = filter === option.value
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => setFilter(option.value)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[0.8125rem] font-bold transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)] ${
                active
                  ? 'wb-chosen'
                  : 'border-white/[0.12] bg-white/[0.04] text-white/70 hover:border-white/25 hover:text-white'
              }`}
            >
              {active && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
              {option.label}
              <span className="font-mono text-[0.6875rem] opacity-60">
                {counts[option.value]}
              </span>
            </button>
          )
        })}
      </div>

      <p aria-live="polite" className="sr-only">
        Showing {counts[filter]} of {counts.all} modules.
      </p>

      <div className="mt-10 space-y-10">
        {visible.map((group) => (
          <div key={group.slug}>
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-white/[0.07] pb-4">
              <h3
                className="text-lg font-semibold text-white"
                style={{ fontFamily: 'var(--wavbee-font-display)' }}
              >
                {group.name}
                <span className="ml-2.5 font-mono text-[0.75rem] font-normal text-white/25">
                  {group.modules.length}
                </span>
              </h3>
              <Link
                href={group.href}
                prefetch
                className="marketing-inline-link inline-flex items-center gap-1.5 text-[0.8125rem] font-semibold hover:!text-[#ff6a1a]"
              >
                {group.link_label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {group.modules.map((module) => {
                const Icon = iconFor(module.key)
                return (
                  <div
                    key={module.key}
                    id={module.key}
                    className="flex scroll-mt-52 sm:scroll-mt-44 flex-col rounded-2xl border border-white/[0.07] bg-white/[0.02] p-5 transition-colors hover:border-white/[0.14]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <IconChip icon={Icon} />
                      <StatusBadge status={module.status} label={module.status_label} />
                    </div>
                    <h4 className="mt-3.5 text-[0.9375rem] font-semibold leading-snug text-white">
                      {module.name}
                    </h4>
                    <p className="mt-2 text-[0.8125rem] leading-[1.7] text-white/60">
                      {module.summary}
                    </p>
                    {module.detail && (
                      <details className="group mt-4">
                        <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-full text-[0.8125rem] font-medium text-white/70 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6a1a] [&::-webkit-details-marker]:hidden">
                          {module.status === 'live' ? 'How it works' : 'What it will do'}
                          <span className="sr-only">: {module.name}</span>
                          <ChevronDown
                            className="h-3.5 w-3.5 transition-transform duration-200 group-open:rotate-180"
                            aria-hidden="true"
                          />
                        </summary>
                        <p className="mt-3 text-[0.8125rem] leading-[1.75] text-white/65">{module.detail}</p>
                        {module.points && module.points.length > 0 && (
                          <ul className="mt-3 flex flex-col gap-2">
                            {module.points.map((point) => (
                              <li key={point} className="flex items-start gap-2">
                                <Check className="mt-[0.2rem] h-3.5 w-3.5 shrink-0 text-[#ff6a1a]" aria-hidden="true" />
                                <span className="text-[0.8125rem] leading-[1.65] text-white/60">{point}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </details>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
